import React, { useMemo } from 'react';
import { Text, StyleSheet, View } from 'react-native';
import {
  buildBookableSpotTypeBreakdown,
  computeBookableSpotsForMap,
  formatBookableSpotTypeBreakdown,
  useRoomLayoutMap,
} from '@glofoxinc/room-layout-map/interactiveSpotBooking';

export type SpotAvailabilityLineData = {
  layoutId: string;
  timeSlotId: string;
  envUrl: string;
  userToken: string;
  availableSuffix: string;
  oneSpotAvailableLabel: string;
  spotsAvailableCountLabel: string;
  availableFallbackLabel: string;
};

type Props = {
  data: SpotAvailabilityLineData | null | undefined;
};

type RoomLayoutQuery = ReturnType<typeof useRoomLayoutMap>;
type RoomLayout = NonNullable<RoomLayoutQuery['data']>;

const FALLBACKS: SpotAvailabilityLineData = {
  layoutId: '',
  timeSlotId: '',
  envUrl: '',
  userToken: '',
  availableSuffix: 'available',
  oneSpotAvailableLabel: '1 spot available',
  spotsAvailableCountLabel: '{count} spots available',
  availableFallbackLabel: 'Available',
};

const FALLBACK_KEYS = Object.keys(FALLBACKS) as (keyof SpotAvailabilityLineData)[];

const styles = StyleSheet.create({
  // Vertical spacing only when content renders — avoids empty Angular host gap.
  wrapper: {
    paddingTop: 16,
    paddingBottom: 4,
  },
  text: {
    fontSize: 17,
    lineHeight: 26,
    // Parent `.spot-availability` sets full brand text color (not muted grey).
    color: 'inherit',
  },
});

function withFallbacks(
  data: SpotAvailabilityLineData | null | undefined
): SpotAvailabilityLineData {
  return FALLBACK_KEYS.reduce((resolved, key) => {
    resolved[key] = data?.[key] || FALLBACKS[key];
    return resolved;
  }, {} as SpotAvailabilityLineData);
}

// A layout only carries availability once the request settled successfully; until
// then the caller must render nothing rather than a generic "Available" label.
function getLoadedLayout(query: RoomLayoutQuery): RoomLayout | null {
  if (query.isLoading || query.isError) {
    return null;
  }
  return query.data ?? null;
}

function countBookableSpots(layout: RoomLayout): number | null {
  if (layout.spots?.length) {
    return computeBookableSpotsForMap(layout.spots).bookableCount;
  }
  return typeof layout.total_bookable_spots === 'number' ? layout.total_bookable_spots : null;
}

function buildAvailabilityLabel(
  layout: RoomLayout,
  labels: SpotAvailabilityLineData
): string {
  const typeBreakdown = layout.spots?.length ? buildBookableSpotTypeBreakdown(layout.spots) : [];
  const typeBreakdownLabel = formatBookableSpotTypeBreakdown(typeBreakdown, labels.availableSuffix);
  if (typeBreakdownLabel) {
    return typeBreakdownLabel;
  }

  const bookableCount = countBookableSpots(layout);
  if (bookableCount === null) {
    return labels.availableFallbackLabel;
  }
  if (bookableCount === 1) {
    return labels.oneSpotAvailableLabel;
  }
  return labels.spotsAvailableCountLabel.replace('{count}', String(bookableCount));
}

export function SpotAvailabilityLine({ data }: Props) {
  const labels = useMemo(() => withFallbacks(data), [data]);

  const layoutQuery = useRoomLayoutMap(
    labels.layoutId,
    labels.envUrl,
    labels.userToken,
    undefined,
    labels.timeSlotId
  );
  const layout = getLoadedLayout(layoutQuery);

  const availableLabel = useMemo(
    () => (layout ? buildAvailabilityLabel(layout, labels) : null),
    [layout, labels]
  );

  if (!data || !availableLabel) {
    return null;
  }

  return (
    <View style={styles.wrapper}>
      <Text
        style={styles.text}
        numberOfLines={2}
        ellipsizeMode="tail"
        testID="spot-availability-line"
      >
        {availableLabel}
      </Text>
    </View>
  );
}
